import {
  FACTOR_CONTRIBUTION_DRILLDOWN_KEY_L1,
  FACTOR_CONTRIBUTION_DRILLDOWN_KEY_L2,
} from '@/constants/common.constant'
import { formatNumber } from '@/utils/utils'
import { IGfpaFactorContributionChartData } from '@interfaces/gfpaReport.interface'
import { getColumnDetail } from '@utils/grid-utils/columnUtils'
import { formatCellValue } from '@utils/tableGridUtils'
import Highcharts, { Options, SeriesOptions } from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useEffect, useState } from 'react'

export type IGFPAFactorCategory = {
  reportType: string
  chartData: IGfpaFactorContributionChartData[]
  handleCurrencySelection: Function
  handleFactorCategorySelection: Function
}

interface CustomSeriesOptions extends SeriesOptions {
  customType?: string
}

export const GFPAFactorContributionChart: React.FunctionComponent<
  IGFPAFactorCategory
> = ({
  reportType,
  chartData,
  handleCurrencySelection,
  handleFactorCategorySelection,
}) => {
  const [chartOptions, setChartOptions] = useState<Options>({
    chart: {
      type: 'bar',
      height: 540,
    },
    title: {
      text: undefined,
    },
    xAxis: {
      lineWidth: 1,
      categories: [],
      tickWidth: 1,
      className: 'factor-contribution-label',
      labels: {
        align: 'center',
        step: 1,
      },
    },
    yAxis: [
      {
        title: {
          text: null,
        },
        lineWidth: 1,
        tickWidth: 1,
        labels: {
          align: 'center',
        },
        gridLineWidth: 1,
      },
    ],
    legend: {
      align: 'right',
      verticalAlign: 'top',
      layout: 'vertical',
      y: 0,
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      headerFormat: '<p>{point.x}</p><br/>',
      pointFormat:
        '<p>{series.name}: <strong>{point.options.custom.value}</strong></p>',
    },
    series: [],
    plotOptions: {
      bar: {
        cursor: 'pointer',
      },
      series: {
        keys: ['y', 'custom.value'],
        point: {
          events: {
            click: function () {
              const type = (this.series.userOptions as CustomSeriesOptions)
                .customType
              if (type === FACTOR_CONTRIBUTION_DRILLDOWN_KEY_L1) {
                handleCurrencySelection(this.category)
              } else if (type === FACTOR_CONTRIBUTION_DRILLDOWN_KEY_L2) {
                handleFactorCategorySelection(this.category)
              }
            },
          },
        },
      },
    },
  })

  useEffect(() => {
    const portFolioData = {
      name: 'Portfolio Factor Contribution',
      data: [] as [number, string][],
      customType: '',
    }
    const benchMarkData = {
      name: 'Benchmark Factor Contribution',
      data: [] as [number, string][],
      customType: '',
    }
    const activeData = {
      name: 'Active Factor Contribution',
      data: [] as [number, string][],
      customType: '',
    }
    const categories: string[] = []

    chartData &&
      chartData.length &&
      chartData.map((data: IGfpaFactorContributionChartData) => {
        portFolioData.data.push([
          Number(formatNumber(data.portfolio)),
          formatCellValue(
            formatNumber(data.portfolio),
            getColumnDetail('portfolio_total_return', reportType).format,
          ),
        ])
        benchMarkData.data.push([
          Number(formatNumber(data.benchmark)),
          formatCellValue(
            formatNumber(data.benchmark),
            getColumnDetail('benchmark_total_return', reportType).format,
          ),
        ])
        activeData.data.push([
          Number(formatNumber(data.difference)),
          formatCellValue(
            formatNumber(data.difference),
            getColumnDetail('active_total_return', reportType).format,
          ),
        ])
        categories.push(data.category)
        portFolioData.customType = data.type as string
        benchMarkData.customType = data.type as string
        activeData.customType = data.type as string
      })

    const updatedChartOptions: any = { ...chartOptions }
    updatedChartOptions.xAxis.categories = categories
    updatedChartOptions.series[0] = portFolioData
    updatedChartOptions.series[1] = benchMarkData
    updatedChartOptions.series[2] = activeData
    updatedChartOptions.plotOptions = {
      bar: {
        cursor:
          portFolioData.customType === FACTOR_CONTRIBUTION_DRILLDOWN_KEY_L1 ||
          portFolioData.customType === FACTOR_CONTRIBUTION_DRILLDOWN_KEY_L2
            ? 'pointer'
            : 'default',
      },
    }
    setChartOptions(updatedChartOptions)
  }, [chartData])

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />
}
